/**
 * Anotar un movimiento de inventario (INV-01, INV-02): llego mercaderia, se
 * vendio por fuera del sitio, o se conto lo que hay.
 *
 * Es la UNICA forma de cambiar el stock de un producto, y a proposito: el
 * negocio llevaba el inventario en una libreta, y lo que la libreta tenia de
 * bueno es que cada cambio quedaba escrito con su motivo. Un campo de "stock"
 * editable a mano no deja rastro, y a los tres meses nadie sabe por que quedan
 * 95 y no 100.
 *
 * Tres botones grandes y no un desplegable: son tres cosas distintas que pasan
 * en la tienda, y se eligen de un vistazo. "Contar" pide el numero que hay de
 * verdad y no "cuanto sumar o restar": el caso comun de un ajuste es "conte y
 * hay 7", y hacer la resta es trabajo de la pantalla. La diferencia la saca el
 * servidor contra el stock de ese instante (`MovimientoStockDto.conteo`).
 */
import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import type { MovimientoStock, TipoMovimientoManual } from '@bandu/compartido';

import { registrarMovimiento } from '../../servicios/administracion.servicio';
import { numero } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';
import { EntradaMonto } from './EntradaMonto';

export interface ProductoDelMovimiento {
  id: number;
  nombre: string;
  stock: number;
  precio: number;
}

export interface FormularioMovimientoProps {
  producto: ProductoDelMovimiento;
  /** El tipo con que abre: los botones de cada fila de Inventario lo traen elegido. */
  tipoInicial?: TipoMovimientoManual;
  onAnotado(movimiento: MovimientoStock): void;
  onCancelar(): void;
}

interface TextosTipo {
  texto: string;
  ayuda: string;
  pregunta: string;
}

/** En el orden de los botones. */
const ORDEN_TIPOS: TipoMovimientoManual[] = ['ingreso', 'venta_externa', 'ajuste'];

const TIPOS: Record<TipoMovimientoManual, TextosTipo> = {
  ingreso: {
    texto: 'Llegó mercadería',
    ayuda: 'Suma al stock.',
    pregunta: '¿Cuántas llegaron?',
  },
  venta_externa: {
    texto: 'Venta por fuera',
    ayuda: 'En la tienda, por WhatsApp o en terreno. Resta del stock y cuenta como venta.',
    pregunta: '¿Cuántas se vendieron?',
  },
  ajuste: {
    texto: 'Contar',
    ayuda: 'Escribe cuántas hay de verdad: la diferencia queda anotada sola. Sirve para un conteo, una merma o una devolución.',
    pregunta: '¿Cuántas hay?',
  },
};

function unidadesDe(texto: string): number | null {
  if (texto.trim() === '') return null;
  const valor = Number(texto);
  return Number.isInteger(valor) && valor >= 0 ? valor : null;
}

export function FormularioMovimiento({
  producto,
  tipoInicial = 'ingreso',
  onAnotado,
  onCancelar,
}: FormularioMovimientoProps) {
  const id = useId();
  const [tipo, setTipo] = useState<TipoMovimientoManual>(tipoInicial);
  const [cantidad, setCantidad] = useState('');
  const [nota, setNota] = useState('');
  const [monto, setMonto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  const elegido = TIPOS[tipo];
  const unidades = unidadesDe(cantidad);
  const contando = tipo === 'ajuste';

  // Contando, el numero escrito ES el stock que queda; en los otros dos, suma o resta.
  const stockResultante =
    unidades === null
      ? null
      : contando
        ? unidades
        : producto.stock + (tipo === 'venta_externa' ? -unidades : unidades);
  const diferencia = stockResultante !== null ? stockResultante - producto.stock : null;

  function problema(): string | null {
    if (unidades === null) return 'Escribe un número entero.';
    if (!contando && unidades === 0) return 'Escribe cuántas unidades, mayor que cero.';
    if (contando && diferencia === 0) return `El stock ya es ${numero(producto.stock)}: no hay nada que anotar.`;
    if (stockResultante !== null && stockResultante < 0) {
      return `Solo hay ${numero(producto.stock)}: no se pueden vender más.`;
    }
    return null;
  }

  async function enviar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    const motivo = problema();
    if (motivo !== null || unidades === null) {
      setFalla(motivo);
      return;
    }

    const montoEscrito = Number(monto.replace(/[.\s$]/g, ''));
    setEnviando(true);
    setFalla(null);

    try {
      const movimiento = await registrarMovimiento({
        productoId: producto.id,
        tipo,
        // Contando viaja el conteo y la cantidad se ignora (la saca el servidor).
        cantidad: contando ? 0 : unidades,
        conteo: contando ? unidades : null,
        nota: nota.trim().length > 0 ? nota.trim() : null,
        montoTotal:
          tipo === 'venta_externa' && monto.trim().length > 0 && Number.isInteger(montoEscrito)
            ? montoEscrito
            : null,
      });
      onAnotado(movimiento);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form
      className="formulario-admin"
      onSubmit={(evento) => {
        void enviar(evento);
      }}
      noValidate
    >
      {falla !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{falla}</span>
        </p>
      ) : null}

      <fieldset className="tipos-movimiento">
        <legend className="solo-lectores">Qué pasó</legend>
        {ORDEN_TIPOS.map((opcion) => (
          <label
            key={opcion}
            className={opcion === tipo ? 'tipo-movimiento elegido' : 'tipo-movimiento'}
          >
            <input
              type="radio"
              name={`${id}-tipo`}
              value={opcion}
              checked={opcion === tipo}
              onChange={() => {
                setTipo(opcion);
                setFalla(null);
              }}
            />
            {TIPOS[opcion].texto}
          </label>
        ))}
      </fieldset>
      <p className="ayuda-campo">{elegido.ayuda}</p>

      <div className="campos-dos">
        <div className="campo">
          <label htmlFor={`${id}-cantidad`}>{elegido.pregunta}</label>
          <input
            id={`${id}-cantidad`}
            className="entrada-grande"
            inputMode="numeric"
            autoComplete="off"
            value={cantidad}
            onChange={(evento) => {
              setCantidad(evento.target.value.replace(/\D/g, ''));
              setFalla(null);
            }}
            required
          />
        </div>

        {tipo === 'venta_externa' ? (
          <div className="campo">
            <label htmlFor={`${id}-monto`}>Total cobrado</label>
            <EntradaMonto
              id={`${id}-monto`}
              valor={monto}
              alCambiar={setMonto}
              placeholder={unidades !== null ? numero(producto.precio * unidades) : ''}
            />
            <p className="ayuda-campo">Vacío usa el precio de lista.</p>
          </div>
        ) : null}
      </div>

      <div className="campo">
        <label htmlFor={`${id}-nota`}>Nota (opcional)</label>
        <input
          id={`${id}-nota`}
          value={nota}
          onChange={(evento) => {
            setNota(evento.target.value);
          }}
          maxLength={300}
          placeholder={
            tipo === 'venta_externa'
              ? 'Quién compró, boleta N°…'
              : contando
                ? 'Conteo mensual, se rompió uno, devolución…'
                : 'De dónde llegó, factura del proveedor…'
          }
        />
      </div>

      {/* Lo que va a pasar, grande: es lo que se revisa antes de anotar. */}
      <p className="resultado-movimiento" role="status">
        <span className="resultado-movimiento-et">Stock</span>
        <strong>{numero(producto.stock)}</strong>
        <Icono nombre="flecha" />
        <strong className={stockResultante === null ? 'pendiente' : undefined}>
          {stockResultante !== null ? numero(stockResultante) : '?'}
        </strong>
        {diferencia !== null && diferencia !== 0 ? (
          <span className={diferencia > 0 ? 'mov-cant entra' : 'mov-cant sale'}>
            ({diferencia > 0 ? '+' : '−'}
            {numero(Math.abs(diferencia))})
          </span>
        ) : null}
      </p>

      <div className="modal-acciones">
        <button type="button" className="btn btn-contorno" onClick={onCancelar} disabled={enviando}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primario" disabled={enviando}>
          {enviando ? 'Anotando…' : 'Anotar'}
        </button>
      </div>
    </form>
  );
}
